import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {Lock, Search, Plus, Edit, Trash2, X, Eye, Filter, FileText, FileSpreadsheet,
  FileOutput, RefreshCcw, ChevronDown, ChevronUp, Upload, Download,
  LayoutGrid, List, LayoutList, CheckCircle, Package, Warehouse,
  Calendar, Hash, User, Tag, XCircle} from 'lucide-react';
import InventaireListPageShell from './components/InventaireListPageShell';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemErpCrudModal from '../../products/saudi/components/QawaemErpCrudModal';
import { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import {
  sliceQawaemStats,
  buildInventaireBulkDeleteFilterAction,
  InventaireTableSelectAllHeader,
  InventaireTableRowCheckbox,
  INVENTAIRE_TABLE_ACTIONS_TH_CLASS,
  buildInventaireImportExportProps,
  buildInventaireColumnVisibilityState,
  buildInventaireQawaemColumnDefs,
  isInventaireColumnVisible,
  inventaireTableColSpan,
  nextInventaireEntityRef,
  InventoryConfigNotice,
  useInventaireListSettings,
  sliceInventaireListItems,
  inventaireRecordInDateRange,
  INVENTAIRE_FORM_GRID,
  fetchInventaireWarehouses,
  buildInventaireWarehouseSelectOptions,
  buildInventaireProductSelectOptions,
  mapInventaireWarehouseSelection,
} from './inventaireListPageUtils';
import { useInventoryConfig } from './useInventoryConfig';
import { useArticlesConfig } from '../articles/useArticlesConfig';
import { fetchCatalogProducts } from '../articles/catalogProductOptions';
import { useSettings } from '../../core/contexts/SettingsContext';
import localApiAdapter from '../../core/services/localApiAdapter';
import { CustomSelect, CustomNumberInput, CustomDatePicker } from '../../shared/components/custom';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import { useTranslation } from 'react-i18next';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import { getBrandLocale } from '../../shared/utils/brandCurrency';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, exactColumnRule, textColumnRule } from '../../shared/utils/columnHeaderFilters';

const NS = 'modules.inventaire.pages.stockReservationPage';
const API_BASE = '/api/inventaire/reservations';

const EMPTY_FORM = {
  reservation_number: '',
  product_id: '',
  product_name: '',
  warehouse_id: '',
  warehouse_name: '',
  qty: '',
  reserved_for: '',
  reserved_until: '',
  status: '',
  notes: '',
};

const StockReservationPage = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`${NS}.${key}`, opts);
  const qawaem = isSaudiBrand();
  const dateLocale = getBrandLocale(i18n.language);
  const formatDate = (value) => {
    if (!value) return p('empty_dash');
    const raw = String(value).split('T')[0];
    return new Date(raw).toLocaleDateString(dateLocale);
  };
  const { settings } = useSettings();
  const inventoryConfig = useInventoryConfig();
  const { dateRange, viewMode, setViewMode, itemsPerPage } = useInventaireListSettings(inventoryConfig);
  useArticlesConfig();

  const STATUS_DEFS = useMemo(
    () => [
      { value: 'active', labelKey: 'status_active' },
      { value: 'fulfilled', labelKey: 'status_fulfilled' },
      { value: 'expired', labelKey: 'status_expired' },
      { value: 'cancelled', labelKey: 'status_cancelled' },
    ],
    []
  );

  const statusOptions = useMemo(
    () => [
      { value: 'active', label: p('status_active'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
      { value: 'fulfilled', label: p('status_fulfilled'), icon: Package, style: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
      { value: 'expired', label: p('status_expired'), icon: Calendar, style: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
      { value: 'cancelled', label: p('status_cancelled'), icon: XCircle, style: 'bg-red-500/20 text-red-400 border-red-500/30' },
    ],
    [p],
  );

  const [reservations, setReservations] = useState([]);
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [viewingRecord, setViewingRecord] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_reservations');
  const [quickFilter, setQuickFilter] = useState('all');
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });

  const generateReservationNumber = useCallback(
    () => nextInventaireEntityRef(settings, 'stockReservation', 'RSV', reservations, (r) => r.reservation_number),
    [settings, reservations],
  );

  const openNewReservationModal = () => {
    if (!inventoryConfig.enableReservations) {
      window.alert(p('notice_reservations_disabled', { defaultValue: 'الحجز معطّل من إعدادات المخزون.' }));
      return;
    }
    setEditingRecord(null);
    setFormData({
      ...EMPTY_FORM,
      reservation_number: inventoryConfig.autoGenerateReservationReference !== false ? generateReservationNumber() : '',
      status: 'active',
    });
    setShowModal(true);
  };

  const productOptions = useMemo(
    () => buildInventaireProductSelectOptions(products),
    [products]
  );

  const warehouseOptions = useMemo(
    () => buildInventaireWarehouseSelectOptions(warehouses),
    [warehouses]
  );


  useEffect(() => {
    loadReservations();
    loadProducts();
    loadWarehouses();
  }, [refreshTrigger]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) {
        setShowImportDropdown(false);
      }
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadReservations = async () => {
    try {
      setLoading(true);
      const response = await localApiAdapter.get(API_BASE);
      const data = response?.data ?? response;
      setReservations(sortRecordsNewestFirst(Array.isArray(data) ? data : []));
    } catch (error) {
      console.error('Error loading reservations:', error);
      setReservations([]);
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      setProducts(await fetchCatalogProducts());
    } catch (error) {
      setProducts([]);
    }
  };

  const loadWarehouses = async () => {
    try {
      setWarehouses(await fetchInventaireWarehouses());
    } catch (error) {
      setWarehouses([]);
    }
  };

  const getStatusLabel = (status) => {
    const def = STATUS_DEFS.find((item) => item.value === status);
    return def ? p(def.labelKey) : status || p('empty_dash');
  };


  const handleStatusChange = async (recordId, newStatus) => {
    try {
      await localApiAdapter.put(`${API_BASE}/${recordId}`, { status: newStatus });
      await loadReservations();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const resetForm = () => {
    setFormData({ ...EMPTY_FORM });
    setEditingRecord(null);
    setShowModal(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.product_id && !formData.product_name.trim()) {
      window.alert(p('alert_required'));
      return;
    }
    if (!formData.status.trim()) {
      window.alert(p('alert_required'));
      return;
    }
    try {
      setLoading(true);
      const reservationNumber = (formData.reservation_number || '').trim() || generateReservationNumber();
      const payload = {
        reservation_number: reservationNumber,
        product_id: formData.product_id || null,
        product_name: formData.product_name.trim() || null,
        warehouse_id: formData.warehouse_id || null,
        warehouse_name: formData.warehouse_name || null,
        qty: formData.qty !== '' ? parseFloat(formData.qty) : 0,
        reserved_for: formData.reserved_for || null,
        reserved_until: formData.reserved_until || null,
        status: formData.status,
        notes: formData.notes || null,
      };
      if (editingRecord) {
        await localApiAdapter.put(`${API_BASE}/${editingRecord.id}`, payload);
        notifyUpdated(showNotify, p('entity_label', { defaultValue: 'الحجز' }));
      } else {
        await localApiAdapter.post(API_BASE, payload);
        notifyCreated(showNotify, p('entity_label', { defaultValue: 'الحجز' }));
      }
      resetForm();
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      console.error('Error saving reservation:', error);
      notifyError(showNotify, error?.response?.data?.error || p('alert_save_error'));
      return;
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({
      reservation_number: record.reservation_number ?? '',
      product_id: record.product_id ?? '',
      product_name: record.product_name ?? '',
      warehouse_id: record.warehouse_id ?? '',
      warehouse_name: record.warehouse_name ?? '',
      qty: record.qty != null ? String(record.qty) : '',
      reserved_for: record.reserved_for ?? '',
      reserved_until: record.reserved_until ? String(record.reserved_until).split('T')[0] : '',
      status: record.status ?? '',
      notes: record.notes ?? '',
    });
    setShowModal(true);
  };

  const handleView = (record) => {
    setViewingRecord(record);
    setShowViewModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        setLoading(true);
        await localApiAdapter.delete(`${API_BASE}/${id}`);
        notifyDeleted(showNotify, p('entity_label', { defaultValue: 'الحجز' }));
        setRefreshTrigger((prev) => prev + 1);
      } catch (error) {
        notifyError(showNotify, error?.response?.data?.error || p('alert_save_error'));
      } finally {
        setLoading(false);
      }
    }
  };

  const handleProductSelect = (productId) => {
    const product = products.find((prod) => String(prod.id) === String(productId));
    setFormData({
      ...formData,
      product_id: productId || '',
      product_name: product?.name || formData.product_name,
    });
  };

  const handleWarehouseSelect = (warehouseId) => {
    const warehouse = warehouses.find((wh) => String(wh.id) === String(warehouseId));
    setFormData({
      ...formData,
      warehouse_id: warehouseId || '',
      warehouse_name: warehouse?.name || formData.warehouse_name,
    });
  };

  const stats = useMemo(() => ({
    total: reservations.length,
    active: reservations.filter((r) => r.status === 'active').length,
    fulfilled: reservations.filter((r) => r.status === 'fulfilled').length,
    expired: reservations.filter((r) => r.status === 'expired').length,
    cancelled: reservations.filter((r) => r.status === 'cancelled').length,
  }), [reservations]);

  const columnFilterRules = useMemo(
    () => [
      textColumnRule('reservation_number'),
      textColumnRule('product_name'),
      textColumnRule('warehouse_name'),
      textColumnRule('qty', (r) => String(r.qty ?? '')),
      textColumnRule('reserved_for'),
      { key: 'reserved_until', getValue: (r) => formatDate(r.reserved_until) },
      exactColumnRule('status', (r) => r.status),
    ],
    [formatDate],
  );

  const baseFilteredReservations = useMemo(() => {
    let list = [...reservations];
    if (dateRange?.start || dateRange?.end) {
      list = list.filter((r) => inventaireRecordInDateRange(dateRange, r.reserved_until, r.created_at));
    }
    if (quickFilter !== 'all') {
      list = list.filter((r) => r.status === quickFilter);
    }
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      list = list.filter(
        (r) =>
          r.reservation_number?.toLowerCase().includes(term) ||
          r.product_name?.toLowerCase().includes(term) ||
          r.warehouse_name?.toLowerCase().includes(term) ||
          r.reserved_for?.toLowerCase().includes(term)
      );
    }
    return list;
  }, [reservations, quickFilter, searchTerm, dateRange]);

  const filteredReservations = useMemo(
    () => applyColumnFilters(baseFilteredReservations, colFilters, columnFilterRules),
    [baseFilteredReservations, colFilters, columnFilterRules],
  );
  const visibleReservations = useMemo(
    () => sliceInventaireListItems(filteredReservations, itemsPerPage),
    [filteredReservations, itemsPerPage],
  );

  const filteredReservationsIds = useMemo(() => filteredReservations.map((r) => r.id), [filteredReservations]);
  const allRowsSelected = filteredReservationsIds.length > 0 && filteredReservationsIds.every((id) => selectedRecords.includes(id));
  const someRowsSelected = selectedRecords.length > 0 && !allRowsSelected;

  const handleSelectAll = () => {
    if (allRowsSelected || someRowsSelected) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(filteredReservationsIds);
    }
  };
  const handleSelectRecord = (id) => {
    setSelectedRecords((prev) => (prev.includes(id) ? prev.filter((rId) => rId !== id) : [...prev, id]));
  };
  const handleBulkDelete = async () => {
    if (!selectedRecords.length) return;
    if (window.confirm(p('bulk_delete_confirm', { count: selectedRecords.length }))) {
      try {
        setLoading(true);
        await Promise.all(selectedRecords.map((id) => localApiAdapter.delete(`${API_BASE}/${id}`)));
        notifyBulkDeleted(showNotify, selectedRecords.length, p('entity_label', { defaultValue: 'الحجز' }));
        setSelectedRecords([]);
        setRefreshTrigger((prev) => prev + 1);
      } catch (error) {
        notifyError(showNotify, p('alert_save_error'));
      } finally {
        setLoading(false);
      }
    }
  };

  const mainTabs = [
    { id: 'all_reservations', name: p('tab_overview'), icon: LayoutGrid },
    { id: 'by_status', name: p('tab_by_status'), icon: CheckCircle },
  ];

  const quickFilterButtons = [
    { id: 'all', name: p('filter_all'), icon: LayoutGrid },
    { id: 'active', name: p('status_active'), icon: CheckCircle },
    { id: 'fulfilled', name: p('status_fulfilled'), icon: Package },
    { id: 'expired', name: p('status_expired'), icon: Calendar },
    { id: 'cancelled', name: p('status_cancelled'), icon: X },
  ];

  const distributionData = STATUS_DEFS.map(({ value, labelKey }) => ({
    name: p(labelKey),
    count: reservations.filter((r) => r.status === value).length,
    color: 'bg-gradient-to-r from-blue-500 to-blue-600',
  }));

  const INVENTAIRE_TABLE_COLUMNS = [
    { key: 'reservation_number', labelKey: 'col_number' },
    { key: 'product_name', labelKey: 'col_product' },
    { key: 'warehouse_name', labelKey: 'col_warehouse' },
    { key: 'qty', labelKey: 'col_qty' },
    { key: 'reserved_for', labelKey: 'col_reserved_for' },
    { key: 'reserved_until', labelKey: 'col_until' },
    { key: 'status', labelKey: 'col_status' },
    { key: 'actions', labelKey: 'col_actions', required: true },
  ];

  const [columnVisibility, setColumnVisibility] = useState(() =>
    buildInventaireColumnVisibilityState(INVENTAIRE_TABLE_COLUMNS),
  );
  const qawaemColumnDefs = useMemo(
    () => buildInventaireQawaemColumnDefs(INVENTAIRE_TABLE_COLUMNS, p, columnVisibility),
    [p, columnVisibility],
  );
  const handleToggleColumn = (key) => {
    setColumnVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const qawaemVisibleColumnCount = qawaemColumnDefs.filter((c) => c.checked !== false).length;
  const isColVisible = (key) => isInventaireColumnVisible(columnVisibility, key);
  const tableColSpan = inventaireTableColSpan(columnVisibility);

  const inventaireStatEntries = useMemo(() => sliceQawaemStats([
    { key: 'stat_total', label: p('stat_total'), value: stats.total, icon: Lock },
    { key: 'status_active', label: p('status_active'), value: stats.active, icon: CheckCircle },
    { key: 'status_fulfilled', label: p('status_fulfilled'), value: stats.fulfilled, icon: Package },
    { key: 'status_expired', label: p('status_expired'), value: stats.expired, icon: Calendar },
  ], 4), [stats, p]);

  const importExportProps = useMemo(() => buildInventaireImportExportProps({
    showImportDropdown,
    setShowImportDropdown,
    showExportDropdown,
    setShowExportDropdown,
    importDropdownRef,
    exportDropdownRef,
    importLabel: t('common.import'),
    exportLabel: t('common.export'),
    generatingLabel: t('common.loading'),
    handlers: {},
  }), [showImportDropdown, showExportDropdown, t]);

  return (
    <>
    <InventoryConfigNotice
      message={!inventoryConfig.enableReservations ? p('notice_reservations_disabled_banner', { defaultValue: 'الحجز معطّل — فعّله من إعدادات المخزون ← الحجز.' }) : null}
    />
    <InventaireListPageShell
      title={p('title')}
      subtitle={p('subtitle')}
      icon={Lock}
      statEntries={inventaireStatEntries}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder={p('search_placeholder')}
      filterActions={buildInventaireBulkDeleteFilterAction({ count: selectedRecords.length, onDelete: handleBulkDelete, p })}
      onCreate={openNewReservationModal}
      createLabel={p('new_item')}
      importExport={importExportProps}
    >
        {viewMode === 'table' ? (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <InventaireTableSelectAllHeader allRowsSelected={allRowsSelected} someRowsSelected={someRowsSelected} onSelectAll={handleSelectAll} p={p} />
                  {isColVisible('reservation_number') && (

                    <TableColumnHeaderFilter columnKey="reservation_number" label={p('col_number')} value={colFilters.reservation_number ?? ''} onChange={(v) => setColFilter('reservation_number', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('product_name') && (

                    <TableColumnHeaderFilter columnKey="product_name" label={p('col_product')} value={colFilters.product_name ?? ''} onChange={(v) => setColFilter('product_name', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('warehouse_name') && (

                    <TableColumnHeaderFilter columnKey="warehouse_name" label={p('col_warehouse')} value={colFilters.warehouse_name ?? ''} onChange={(v) => setColFilter('warehouse_name', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('qty') && (

                    <TableColumnHeaderFilter columnKey="qty" label={p('col_qty')} value={colFilters.qty ?? ''} onChange={(v) => setColFilter('qty', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-white" />
                  )}
                  {isColVisible('reserved_for') && (

                    <TableColumnHeaderFilter columnKey="reserved_for" label={p('col_reserved_for')} value={colFilters.reserved_for ?? ''} onChange={(v) => setColFilter('reserved_for', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('reserved_until') && (

                    <TableColumnHeaderFilter columnKey="reserved_until" label={p('col_until')} value={colFilters.reserved_until ?? ''} onChange={(v) => setColFilter('reserved_until', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('status') && (

                    <TableColumnHeaderFilter columnKey="status" label={t('common.status')} kind="select" options={statusOptions} value={colFilters.status ?? ''} onChange={(v) => setColFilter('status', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-white" />
                  )}
                  <QawaemTableHeaderActionsCell
                    label={t('common.actions')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                    thClassName={INVENTAIRE_TABLE_ACTIONS_TH_CLASS}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {loading ? (
                  <tr><td colSpan={tableColSpan} className="text-center py-12 text-white/60">{t('common.loading')}</td></tr>
                ) : visibleReservations.length > 0 ? (
                  visibleReservations.map((record, index) => (
                    <tr key={record.id} className="hover:bg-white/5">
                      <InventaireTableRowCheckbox checked={selectedRecords.includes(record.id)} onChange={() => handleSelectRecord(record.id)} />
                      {isColVisible('reservation_number') && (

                        <td className="px-6 py-4 text-white font-mono font-semibold">{record.reservation_number}</td>

                      )}
                      {isColVisible('product_name') && (

                        <td className="px-6 py-4 text-white">{record.product_name}</td>

                      )}
                      {isColVisible('warehouse_name') && (

                        <td className="px-6 py-4 text-white/80">{record.warehouse_name || p('empty_dash')}</td>

                      )}
                      {isColVisible('qty') && (

                        <td className="px-6 py-4 text-right text-white">{record.qty}</td>

                      )}
                      {isColVisible('reserved_for') && (

                        <td className="px-6 py-4 text-white/80">{record.reserved_for || p('empty_dash')}</td>

                      )}
                      {isColVisible('reserved_until') && (

                        <td className="px-6 py-4 text-white/80">{formatDate(record.reserved_until)}</td>

                      )}
                      {isColVisible('status') && (

                        <td className="px-6 py-4 text-center"><QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status') || t('common.change_status')} /></td>

                      )}
                      <td className="px-6 py-4">
                        <QawaemErpRowActions
                          onView={() => handleView(record)}
                          onEdit={() => handleEdit(record)}
                          onDelete={() => handleDelete(record.id)}
                          viewTitle={t('common.view')}
                          editTitle={t('common.edit')}
                          deleteTitle={t('common.delete')}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={tableColSpan} className="text-center py-12 text-white/60">
                      <Lock size={48} className="mx-auto mb-4 text-white/30" />
                      <p className="text-lg">{p('empty_title')}</p>
                      <p className="text-sm mt-2">{p('empty_hint')}</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            {visibleReservations.length > 0 ? visibleReservations.map((record, index) => (
              <motion.div key={record.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 h-full min-w-0">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-white font-mono font-semibold">{record.reservation_number}</span>
                      <QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status') || t('common.change_status')} />
                    </div>
                    <p className="text-white font-semibold">{record.product_name}</p>
                    <p className="text-white/60 text-sm mt-1">{record.warehouse_name || p('empty_dash')} · {p('col_qty')}: {record.qty}</p>
                    {record.reserved_for && <p className="text-white/50 text-sm mt-1">{p('col_reserved_for')}: {record.reserved_for}</p>}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => handleView(record)}
                      onEdit={() => handleEdit(record)}
                      onDelete={() => handleDelete(record.id)}
                      viewTitle={t('common.view')}
                      editTitle={t('common.edit')}
                      deleteTitle={t('common.delete')}
                    />
                  </div>
                </div>
              </motion.div>
            )) : (
              <div className="text-center py-12 text-white/60">
                <Lock size={48} className="mx-auto mb-4 text-white/30" />
                <p>{p('empty_title')}</p>
              </div>
            )}
          </div>
        )}
    </InventaireListPageShell>

      {showModal && (
        <QawaemErpCrudModal
          open={showModal}
          onClose={resetForm}
          title={editingRecord ? p('modal_edit') : p('modal_new')}
          icon={Lock}
          formId="reservation-form"
          saveLabel={t('common.save')}
          showSaveButton
          saving={loading}
          maxWidth="900px"
          testId="reservation-crud-modal"
        >
              <form id="reservation-form" onSubmit={handleSubmit}>
                <div className={`${INVENTAIRE_FORM_GRID} mt-4`}>
                  <div>
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_number')}</label>
                    <input
                      type="text"
                      required
                      value={formData.reservation_number}
                      onChange={(e) => setFormData({ ...formData, reservation_number: e.target.value })}
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                      placeholder={p('placeholder_number')}
                      dir="ltr"
                    />
                  </div>
                  <div>
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_status')}</label>
                    <CustomSelect value={formData.status} onChange={(val) => setFormData({ ...formData, status: val || '' })} options={statusOptions} placeholder={p('field_status')} searchable creatable autoSelect={false} />
                  </div>
                  <div>
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_product')}</label>
                    <CustomSelect value={formData.product_id} onChange={handleProductSelect} options={productOptions} placeholder={p('field_product')} searchable creatable autoSelect={false} />
                  </div>
                  <div>
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_warehouse')}</label>
                    <CustomSelect value={formData.warehouse_id} onChange={handleWarehouseSelect} options={warehouseOptions} placeholder={p('field_warehouse')} searchable creatable autoSelect={false} />
                  </div>
                  <div>
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_qty')}</label>
                    <CustomNumberInput value={formData.qty} onChange={(val) => setFormData({ ...formData, qty: val })} placeholder={p('placeholder_zero')} />
                  </div>
                  <div>
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_reserved_for')}</label>
                    <input type="text" value={formData.reserved_for} onChange={(e) => setFormData({ ...formData, reserved_for: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder={p('placeholder_reserved_for')} />
                  </div>
                  <div>
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_reserved_until')}</label>
                    <CustomDatePicker value={formData.reserved_until} onChange={(val) => setFormData({ ...formData, reserved_until: val || '' })} />
                  </div>
                  <div className="md:col-span-3">
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_notes')}</label>
                    <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={3} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder={p('placeholder_notes')} />
                  </div>
                </div>
              </form>
        </QawaemErpCrudModal>
      )}

      {showViewModal && viewingRecord ? (
        <QawaemErpViewModal
          open
          onClose={() => setShowViewModal(false)}
          title={viewingRecord.reservation_number}
          icon={Lock}
          maxWidth="900px"
          testId="reservation-view-modal"
        >
            <div className={INVENTAIRE_FORM_GRID}>
              <div><p className="text-white/60 text-xs uppercase mb-1">{p('col_product')}</p><p className="text-white font-semibold">{viewingRecord.product_name}</p></div>
              <div><p className="text-white/60 text-xs uppercase mb-1">{p('col_warehouse')}</p><p className="text-white">{viewingRecord.warehouse_name || p('empty_dash')}</p></div>
              <div><p className="text-white/60 text-xs uppercase mb-1">{p('col_qty')}</p><p className="text-white">{viewingRecord.qty}</p></div>
              <div><p className="text-white/60 text-xs uppercase mb-1">{p('col_reserved_for')}</p><p className="text-white">{viewingRecord.reserved_for || p('empty_dash')}</p></div>
              <div><p className="text-white/60 text-xs uppercase mb-1">{p('col_until')}</p><p className="text-white">{formatDate(viewingRecord.reserved_until)}</p></div>
              <div><p className="text-white/60 text-xs uppercase mb-1">{t('common.status')}</p><QawaemClickableStatusBadge status={viewingRecord.status} recordId={viewingRecord.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status') || t('common.change_status')} /></div>
            </div>
            {viewingRecord.notes && <div className="mt-4 bg-white/5 rounded-xl p-4 border border-white/20"><p className="text-white/70 text-sm">{viewingRecord.notes}</p></div>}
        </QawaemErpViewModal>
      ) : null}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </>
  );
};

export default StockReservationPage;
